'use client';

/**
 * FilterCombobox — the searchable single-select behind a list page's filter bar.
 *
 * Generalised out of JobFilterCombobox, which invented this pattern for the
 * applications page's job filter and was then the only control on that toolbar
 * you could type into. The Client filter beside it was a plain <Select>, so with
 * a few dozen clients the only way to reach one was to scroll a list — two
 * controls sitting side by side, doing the same job, behaving differently. The
 * fix is one component, not a second copy.
 *
 * Built on Popover + a filter input (mirroring platform-command-menu's
 * keyboard/a11y pattern) rather than cmdk. Small lists filter in memory;
 * onSearchChange lets a caller supply bounded server pages and a footer.
 *
 * Lives in components/ui — it wraps native <input>/<button>, which is precisely
 * what a shared primitive is for (and what the native-control ratchet exempts
 * this directory to allow).
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Search, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface FilterComboboxOption {
  id: string;
  label: string;
  /** Saved record details, displayed in results and included in local search. */
  description?: string;
}

export interface FilterComboboxProps {
  id?: string;
  disabled?: boolean;
  options: FilterComboboxOption[];
  /** Selected id, or '' for the "all" row. */
  value: string;
  /** Called with the new id, or '' when the "all" row is picked. */
  onChange: (id: string) => void;
  /** The clear row's label, and the trigger's label when nothing is selected. */
  allLabel: string;
  /** Label to show when `value` isn't in `options` (e.g. a closed job from a shared link). */
  selectedLabelFallback?: string;
  icon?: LucideIcon;
  searchPlaceholder?: string;
  searchMaxLength?: number;
  /** Accessible name for the search box and the list. Defaults from `allLabel`. */
  searchLabel?: string;
  /**
   * Shown when the list comes back empty for a query.
   *
   * There is deliberately no "the list itself is empty" counterpart: the clear
   * row is always present when there is no query, so an empty rendered list can
   * only ever be the result of one — a distinction the previous version drew
   * and could never actually show.
   */
  noMatchLabel?: string;
  loading?: boolean;
  /** Optional server search: supplied options are already filtered. */
  onSearchChange?: (query: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Pagination/status controls remain outside the listbox's options. */
  footer?: ReactNode;
  className?: string;
  /** Popover width. Wider than the trigger by default — the list holds the long form of a truncated label. */
  contentClassName?: string;
}

// Synthetic id for the "all" clear row — never collides with a real UUID.
const ALL = '__all__';

export function FilterCombobox({
  id, disabled = false,
  options: items,
  value,
  onChange,
  allLabel,
  selectedLabelFallback,
  icon: Icon,
  searchPlaceholder,
  searchMaxLength,
  searchLabel,
  noMatchLabel,
  loading,
  className,
  contentClassName,
  onSearchChange, open: controlledOpen, onOpenChange, footer,
}: FilterComboboxProps) {
  const [internalOpen, setOpen] = useState(false);
  const open = !disabled && (controlledOpen ?? internalOpen);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  // Per-instance, so two comboboxes on one toolbar cannot both claim the same
  // element ids — aria-activedescendant would then point at the wrong list.
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  const selectedLabel = value
    ? items.find((o) => o.id === value)?.label ?? selectedLabelFallback ?? 'Selected'
    : allLabel;

  // The clear row (when it matches the query) followed by the filtered items.
  // ONE flat list so keyboard nav indexes line up 1:1 with what is rendered.
  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows: (FilterComboboxOption & { isAll?: boolean })[] = [];
    if (!q || allLabel.toLowerCase().includes(q)) rows.push({ id: ALL, label: allLabel, isAll: true });
    for (const o of items) {
      if ((!loading || !onSearchChange) && (onSearchChange || !q || [o.label, o.description].join(' ').toLowerCase().includes(q))) rows.push(o);
    }
    return rows;
  }, [items, query, allLabel, onSearchChange, loading]);

  // A server page can shrink while the input stays open. Keyboard actions and
  // its accessible highlight must refer to an option in the current page.
  const visibleActiveIndex = Math.max(0, Math.min(activeIndex, options.length - 1));

  useEffect(() => {
    if (!open) return;
    const activeOption = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]')[visibleActiveIndex];
    activeOption?.scrollIntoView?.({ block: 'nearest' });
  }, [open, visibleActiveIndex, options]);

  const listLabel = searchLabel ?? allLabel;

  const commit = (id: string) => {
    onChange(id === ALL ? '' : id);
    setOpen(false);
    onOpenChange?.(false);
    setQuery('');
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(Math.min(visibleActiveIndex + 1, Math.max(0, options.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(Math.max(visibleActiveIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[visibleActiveIndex];
      if (opt) commit(opt.id);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        onOpenChange?.(o);
        if (o) {
          setQuery('');
          onSearchChange?.('');
          setActiveIndex(0);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          disabled={disabled}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-haspopup="listbox"
          // Explicit, because `combobox` is NOT a name-from-content role: the
          // visible "All clients" inside this button contributes nothing to the
          // accessible name, so without this a screen reader announces a bare
          // "combobox" — and the applications toolbar has TWO of these side by
          // side, which would then be indistinguishable from each other. Names
          // the filter AND its current value, the way the sighted reading does.
          aria-label={`${listLabel}: ${selectedLabel}`}
          className={cn('w-full sm:w-[180px] justify-start font-normal', className)}
        >
          {Icon && <Icon className="w-4 h-4 mr-1.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown className="ml-auto w-4 h-4 shrink-0 text-muted-foreground/60" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn('w-[280px] p-0', contentClassName)}>
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={query}
            maxLength={searchMaxLength}
            onChange={(e) => {
              setQuery(e.target.value);
              onSearchChange?.(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onInputKeyDown}
            placeholder={searchPlaceholder ?? `Search ${listLabel.toLowerCase()}…`}
            aria-label={`Search ${listLabel.toLowerCase()}`}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={options[visibleActiveIndex] ? `${listId}-opt-${visibleActiveIndex}` : undefined}
            className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ul ref={listRef} id={listId} role="listbox" aria-label={listLabel} aria-busy={loading || undefined} className="max-h-64 overflow-y-auto p-1">
          {loading && !onSearchChange ? (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</li>
          ) : options.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              {loading ? 'Loading…' : noMatchLabel ?? 'No matches.'}
            </li>
          ) : (
            options.map((opt, index) => {
              const isSelected = opt.id === ALL ? !value : opt.id === value;
              return (
                <li key={opt.id} role="presentation">
                  <button
                    type="button"
                    id={`${listId}-opt-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={-1}
                    onClick={() => commit(opt.id)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm',
                      index === visibleActiveIndex ? 'bg-accent text-accent-foreground' : 'text-foreground',
                      opt.isAll && 'font-medium',
                    )}
                  >
                    <Check className={cn('w-4 h-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block truncate">{opt.label}</span>
                      {opt.description ? <span className={cn('block break-words text-xs', index === visibleActiveIndex ? 'text-accent-foreground' : 'text-muted-foreground')}>{opt.description}</span> : null}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
        {footer}
      </PopoverContent>
    </Popover>
  );
}
